import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Box, Container, Snackbar } from '@mui/material';
import { ConfirmModal } from 'components/Modal';
import {
  createCompany,
  updateReferrer,
  getCompanyDocById,
  activateReferrer,
  deactivateReferrer,
} from 'apis';

import { useAuth } from 'app/ProviderAuth';
import { Button } from 'components';
import { ReferrerForm } from '../components/ReferrerForm';
import { Referrer } from 'types';
import { PauseIcon } from 'resource';

interface EditReferrerProfilePageProps{
  referrerData: Referrer;
  setReferrer: React.Dispatch<React.SetStateAction<Referrer | undefined>>;
  referrerDocID: string;
  visible: boolean;
  backToProfile: () => void;
}
export const EditReferrerProfilePage: React.FC<EditReferrerProfilePageProps> =
({referrerData:referrer, setReferrer, referrerDocID, visible, backToProfile}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isNewCompany, setIsNewCompany] = useState(true);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [msgBoxOpen, setMsgBoxOpen] = useState(false);
  const [isReferrerActive, setIsReferrerActive] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    getValues,
    setValue,
    reset,
  } = useForm<Referrer>({
    defaultValues: {
      companyId: '',
      name: '',
      company: '',
      department: '',
      jobTitle: '',
      referralEmail: '',
      sponsorship: '',
      showLinkedIn: '',
      internship: '',
      linkedIn: '',
      selfIntroduction: '',
      applicantNote: '',
      location: '',
      role: '',
      careerPage: '',
      isActivated: true,
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (user && referrer && visible) {
      setIsReferrerActive(referrer.isActivated ?? true);
      setMsgBoxOpen(!(referrer.isActivated ?? true));
      reset({
        companyId: referrer.companyId,
        name: referrer.name,
        company: referrer.company,
        department: referrer.department ?? '',
        jobTitle: referrer.jobTitle,
        referralEmail: referrer.referralEmail,
        sponsorship: referrer.sponsorship ?? 'yes',
        showLinkedIn: referrer.showLinkedIn ?? 'yes',
        internship: referrer.internship ?? 'yes',
        linkedIn: referrer.linkedIn ?? '',
        selfIntroduction: referrer.selfIntroduction ?? '',
        applicantNote: referrer.applicantNote ?? '',
        location: referrer.location ?? '',
        role: referrer.role ?? '',
        careerPage: referrer.careerPage ?? '',
        isActivated: referrer.isActivated ?? true,
      });
    }
  }, [user, referrer, visible, setValue, reset]);

  const onSubmit: SubmitHandler<Referrer> = async (data) => {
    const referrerData: Referrer = data as Referrer;
    referrerData.company = referrerData.company.trim();
    referrerData.location = referrerData.location?.trim();
    referrerData.referralEmail = referrerData.referralEmail.trim();
    await updateReferrer(referrerDocID, referrerData);

    var companyDoc = await getCompanyDocById(referrerData.companyId);
    if (isNewCompany && !companyDoc.exists()) {
      const { company, companyId, sponsorship } = referrerData;
      companyDoc = await createCompany(company, companyId, sponsorship!);
    }
    setReferrer(referrerData);
    setSnackBarOpen(true);
  };

  const handleActivate = () => {
    if (isReferrerActive === false) {
      activateReferrer(referrerDocID);
      setIsReferrerActive(true);
      const updatedReferrer = {
        ...referrer,
        isActivated: true,
      };
      setReferrer(updatedReferrer);
      setReferrer(referrer);
    } else {
      setMsgBoxOpen(true);
    }
  };

  const handleConfirm = (confirm?: boolean) => {
    if (confirm) {
      const updatedReferrer = {
        ...referrer,
        isActivated: !isReferrerActive,
      };
      setReferrer(updatedReferrer);
      if (isReferrerActive) {
        deactivateReferrer(referrerDocID);
        backToProfile();
      } else {
        activateReferrer(referrerDocID);
      }
    }
    if (!confirm && !isReferrerActive) backToProfile();
    setMsgBoxOpen(false);
  };
  const backToCompany = () => {
    navigate('/companies');
  };
  return (
    <Box my={5}>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoHideDuration={5000}
        onClose={() => setSnackBarOpen(false)}
        open={snackBarOpen}
        message="成功更新推薦人資料"
        action={
          <Button color="secondary" onClick={backToCompany}>
            回首頁
          </Button>
        }
      />
      <Container maxWidth="md" sx={{ my: 4 }}>
        <ReferrerForm
          control={control}
          errors={errors}
          handleSubmit={handleSubmit(onSubmit)}
          isSubmitting={isSubmitting}
          submitted={snackBarOpen}
          setIsNewCompany={setIsNewCompany}
          setValue={setValue}
          getValues={getValues}
          disableReferralComponent= {
            <Button
              startIcon={ <PauseIcon />}
              onClick={handleActivate}
              sx={{height: '40px'}}
              color="secondary"
            >
              {isReferrerActive ? '暫停推薦人身份' : '重新啟用內推者身份'}
            </Button>
          }
        />
        <ConfirmModal
          title={isReferrerActive ? '暫停推薦人身份' : '重啟推薦人身份'}
          name={
            isReferrerActive
              ? '確定要暫停內推者身份嗎?\n確認後會自動拒絕所有申請'
              : '您已經暫停內推者身份，是否要重新啟用內推者身份'
          }
          open={msgBoxOpen}
          onClose={() => setMsgBoxOpen(false)}
          onProceed={handleConfirm}
        />
      </Container>
    </Box>
  );
};
